import type { FuzzyBounds, FuzzyValue } from '../util/fuzzyNumbers';
import { addFuzzy, integerToFuzzy, fractionToFuzzy, multiplyFuzzy, divideFuzzy, subtractFuzzy, compareFuzzy, maxFuzzy } from '../util/fuzzyMath';
import type { TamsClient, TamsSegment } from '../types/tams';
import type { Logger } from '../types/logger';
import { invariant } from '../util/invariant';
import { frac } from '../util/integerMath';

export function parseTamsTimerange(timerange: string): FuzzyBounds {
  // [0:0_10:0)
  let startInclusive : boolean;
  const startChar = timerange[0];
  switch (startChar) {
    case '[':
      startInclusive = true;
      break;
    case '(':
      startInclusive = false;
      break;
    default:
      throw new Error(`Start of range character ${startChar}`);
  }

  let endInclusive : boolean;
  const endChar = timerange[timerange.length - 1];
  switch (endChar) {
    case ']':
      endInclusive = true;
      break;
    case ')':
      endInclusive = false;
      break;
    default:
      throw new Error(`Start of range character ${endChar}`);
  }

  const meat = timerange.slice(1, timerange.length - 1);
  const [startSpec, endSpec] = meat.split('_');

  const parseSpec = (s: string): FuzzyValue => {
    const [seconds, nanoseconds] = s.split(':').map((part) => parseInt(part));
    if (!isFinite(seconds) || !isFinite(nanoseconds)) {
      throw new Error(`Bad time spec: ${s}`);
    }
    return addFuzzy(integerToFuzzy(seconds), fractionToFuzzy(frac(nanoseconds, 1_000_000_000)));
  };

  let start = parseSpec(startSpec);
  let end = parseSpec(endSpec);
  const nanoSecond = fractionToFuzzy(frac(1, 1_000_000_000));

  // For FuzzyBounds start is assumed to be inclusive. If it isn't we need to
  // add a nano second to indicate this (I guess)
  if (!startInclusive) {
    start = addFuzzy(start, nanoSecond);
  }
  // For fuzzyBounds end is assumed to be exclusive. If it isn't twe need to
  // add a nano second to indicate this (I guess)
  if (endInclusive) {
    end = addFuzzy(start, nanoSecond);
  }

  return { start, end };
}

function serializeTamsTimerangeValue(value: FuzzyValue): string {
  const seconds = Math.floor(value.approximate);
  const nanoseconds = Math.floor((value.approximate - seconds) * 1_000_000_000);
  return `${seconds}:${nanoseconds}`;
}

export function serializeTamsTimerange(
  timerange: FuzzyBounds,
  start: 'inclusive' | 'exclusive' = 'inclusive',
  end: 'inclusive' | 'exclusive' = 'exclusive',
): string {
  return [
    (start === 'inclusive') ? '[' : '(',
    serializeTamsTimerangeValue(timerange.start),
    '_',
    serializeTamsTimerangeValue(timerange.end),
    (end === 'inclusive') ? ']' : ')',
  ].join('');
}

export async function tamsBinarySearchLastSegment(
  logger: Logger,
  tamsClient: TamsClient,
  flowId: string,
  startTime: FuzzyValue | null,
  provideUrls: boolean,
): Promise<TamsSegment | null> {
  logger.info(`Binary search for TAMS end segment: flowId=${flowId}`);

  let high : FuzzyValue;
  if (startTime === null) {
    const [firstSegment] = await tamsClient.getSegments({ flowId, limit: 1 });
    if (firstSegment === undefined) {
      return null;
    }
    const range = parseTamsTimerange(firstSegment.timerange);
    high = range.end;
  } else {
    high = startTime;
  }

  let low : FuzzyValue = integerToFuzzy(0);
  let jump : FuzzyValue = integerToFuzzy(0);

  let segmentDuration : FuzzyValue = integerToFuzzy(1);

  let endSegment : TamsSegment | null = null;

  // 1. Double high until no segments
  // 2. Keep last position that resulted in segment in low
  while (true) {
    logger.debug(`Checking position ${high.approximate.toFixed(2)}`);

    const timerange =
      serializeTamsTimerange(
        { start: high, end: high },
        'inclusive',
        'inclusive'
      );

    const segments = await tamsClient.getSegments({ flowId, timerange, accept_get_urls: '' });

    if (segments.length === 0) {
      logger.info(`Went past end at ${high.approximate.toFixed(2)}`);
      break;
    }

    endSegment = segments[0];

    const segmentRange = parseTamsTimerange(segments[0].timerange);
    segmentDuration = subtractFuzzy(segmentRange.end, segmentRange.start);

    low = high;
    jump = maxFuzzy(multiplyFuzzy(jump, integerToFuzzy(2)), segmentDuration);
    logger.debug(`Not gone past end yet. Will increase current position by ${jump.approximate.toFixed(2)}`);
    high = addFuzzy(high, jump);
  }

  // 3. Binary search to find the last segment between low and high
  while (true) {
    const middle = divideFuzzy(addFuzzy(high, low), integerToFuzzy(2));

    const timerange =
      serializeTamsTimerange(
        { start: middle, end: middle },
        'inclusive',
        'inclusive'
      );

    const segments = await tamsClient.getSegments({ flowId, timerange, accept_get_urls: '' });

    if (compareFuzzy(segmentDuration, subtractFuzzy(high, low)) > 0) {
      if (provideUrls) {
        const { object_id } = segments[0];
        const [segment] = await tamsClient.getSegments({ flowId, object_id });
        invariant(segment !== undefined, `Segment with object id ${object_id} no longer exists`);
        return segment;
      }
      return segments[0] ?? endSegment;
    }

    if (segments.length === 0) {
      high = middle;
      logger.info(`Still after end at ${high.approximate.toFixed(2)}`);
      continue;
    }

    endSegment = segments[0];

    low = middle;
    logger.info(`Found segment but search interval is still to large [${low.approximate.toFixed(2)}, ${high.approximate.toFixed(2)}>`);
  }
}

